import {UseFormReturn} from 'react-hook-form';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {PresentationSettingsFormType} from '../../../../../incase-domain/validators/presentation/presentationSchema';
import ToggleSwitch from '../../../../../incase-domain/components/toggleSwitch/ToggleSwitch';
import DebouncedNumberInput from '../../../../../components/slides/highlighted/common/debounced/DebounceNumberInput';
import {initialGameConfig} from '../../../../../constants';

interface GamificationSettingsProps {
    useForm: UseFormReturn<PresentationSettingsFormType>;
}

function GamificationSettings({useForm}: GamificationSettingsProps): JSX.Element {
    const {t} = useTranslation();
    const {watch, setValue} = useForm;

    const presentation = watch('presentation');

    const isGamificationOn = presentation.gameConfig?.isGamificationOn ?? initialGameConfig.isGamificationOn;

    const handleToggleGamification = (): void => {
        setValue('presentation.gameConfig.isGamificationOn', !isGamificationOn);
    };

    const handleSetCorrectPoints = (value: number): void => {
        setValue('presentation.gameConfig.correctPointsPlus', value);
    };

    const handleSetWrongPoints = (value: number): void => {
        setValue('presentation.gameConfig.wrongPointsMinus', value);
    };

    const handleSetGameClock = (value: number): void => {
        setValue('presentation.gameConfig.gameClock', value);
    };

    return (
        <>
            <div className="c-verticalNavigation__header u-flex u-h-between">
                <div className="c-verticalNavigation__header_title">{t('presEditPage.header.gamificationModal.title')}</div>
                <ToggleSwitch color="info" id="gamification-options" isOn={isGamificationOn} handleToggle={handleToggleGamification} />
            </div>

            <div className="u-flex u-flex-col u-gap-3">
                <div>
                    <div className="u-flex u-gap-2 u-v-center u-h-between">
                        <div>
                            <strong>{t('presEditPage.header.gamificationModal.correctAnswer')}</strong>
                            <p>{t('presEditPage.header.gamificationModal.correctAnswerDescription')}</p>
                        </div>
                        <div className="u-flex u-v-center">
                            <DebouncedNumberInput
                                onChange={handleSetCorrectPoints}
                                min={0}
                                value={presentation.gameConfig?.correctPointsPlus ?? initialGameConfig.correctPointsPlus}
                                disabled={!isGamificationOn}
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <div className="u-flex u-gap-2 u-v-center u-h-between">
                        <div>
                            <strong>{t('presEditPage.header.gamificationModal.incorrectAnswer')}</strong>
                            <p>{t('presEditPage.header.gamificationModal.incorrectAnswerDescription')}</p>
                        </div>
                        <div className="u-flex u-v-center">
                            <DebouncedNumberInput
                                onChange={handleSetWrongPoints}
                                min={0}
                                value={presentation.gameConfig?.wrongPointsMinus ?? initialGameConfig.wrongPointsMinus}
                                disabled={!isGamificationOn}
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <div className="u-flex u-gap-2 u-v-center u-h-between">
                        <div>
                            <strong>{t('presEditPage.header.gamificationModal.gameClock')}</strong>
                            <p>{t('presEditPage.header.gamificationModal.gameClockDescription')}</p>
                        </div>
                        <div className="u-flex u-v-center">
                            <DebouncedNumberInput
                                onChange={handleSetGameClock}
                                min={0}
                                value={presentation.gameConfig?.gameClock ?? initialGameConfig.gameClock}
                                disabled={!isGamificationOn}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default GamificationSettings;
